import type { Stinger, UiSound } from './audioService';
import { bell, brass, organ } from './instruments';
import { jitterGain, jitterRatio } from './mix';
import type { Mixer } from './mixer';
import { bellPartials, midiToHz, noiseBurst, ringTick, tone } from './synth';

/**
 * Menu sounds and mission/heat stingers. Stingers are original little minor-key 1970s brass/organ
 * motifs defined as data (`STINGER_SCORES`) plus a few hand-built ones (busted, wasted, districtCard).
 */

export type ScoreInst = 'brass' | 'organ' | 'bell' | 'ping';

export interface ScoreNote {
  /** Seconds from stinger start. */
  t: number;
  /** MIDI note(s). */
  midi: number | readonly number[];
  dur: number;
  inst: ScoreInst;
  vel: number;
}

const n = (t: number, midi: number | readonly number[], dur: number, inst: ScoreInst, vel = 1): ScoreNote => ({ t, midi, dur, inst, vel });

/** Note-based stingers (A minor / G major centred). */
export const STINGER_SCORES: Partial<Record<Stinger, readonly ScoreNote[]>> = {
  // Tense two-bar riser over an organ drone, ending on an E7 hit.
  missionStart: [
    n(0, 45, 2.9, 'organ', 0.9),
    n(0.0, [57, 60], 0.2, 'brass', 0.8),
    n(0.3, [57, 60], 0.2, 'brass', 0.8),
    n(0.6, [60, 63], 0.2, 'brass', 0.85),
    n(0.9, [60, 63], 0.2, 'brass', 0.85),
    n(1.2, [62, 65], 0.2, 'brass', 0.9),
    n(1.5, [63, 66], 0.2, 'brass', 0.95),
    n(1.8, [64, 67], 0.2, 'brass', 1),
    n(2.1, [52, 56, 59, 62], 0.8, 'brass', 1.1),
    n(2.1, 40, 0.8, 'organ', 1),
  ],
  // Brassy major-ish resolution (G), ~1.5 s.
  missionSuccess: [
    n(0.0, [55, 59, 62], 0.22, 'brass', 0.9),
    n(0.24, [60, 64, 67], 0.22, 'brass', 0.95),
    n(0.48, [62, 67, 71], 0.22, 'brass', 1),
    n(0.72, [67, 71, 74], 0.75, 'brass', 1.1),
    n(0.72, 43, 0.75, 'organ', 1),
    n(0.76, 86, 0.7, 'bell', 0.7),
  ],
  // Descending minor chords sinking onto a low organ.
  missionFail: [
    n(0.0, [57, 60, 64], 0.3, 'brass', 0.9),
    n(0.35, [55, 58, 62], 0.3, 'brass', 0.9),
    n(0.7, [53, 56, 60], 0.3, 'brass', 0.95),
    n(1.05, [52, 55, 59], 0.9, 'brass', 1),
    n(0.0, 45, 2.0, 'organ', 0.9),
  ],
  heatUp: [n(0.0, 86, 0.22, 'ping', 1), n(0.1, 91, 0.3, 'ping', 1.1)],
  heatDown: [n(0.0, 79, 0.4, 'organ', 0.5), n(0.22, 74, 0.5, 'organ', 0.45), n(0.22, 86, 0.4, 'bell', 0.3)],
  toast: [n(0.0, 88, 0.55, 'bell', 1)],
};

/** Approximate length (s) of every stinger, used for ducking. */
export const STINGER_DURATION: Record<Stinger, number> = {
  missionStart: 3.0,
  missionSuccess: 1.55,
  missionFail: 2.0,
  busted: 1.9,
  wasted: 2.1,
  heatUp: 0.45,
  heatDown: 0.8,
  toast: 0.6,
  districtCard: 1.7,
};

/** Ducking depth for each stinger (short UI-ish ones barely duck). */
export function stingerDuck(s: Stinger): number {
  return s === 'toast' || s === 'heatUp' || s === 'heatDown' ? 0.35 : 0.5;
}

export const UI_SOUNDS: readonly UiSound[] = ['click', 'confirm', 'back', 'tick', 'stamp', 'cash', 'denied', 'whoosh', 'type', 'ring', 'message', 'switch'];

const r = (lo: number, hi: number): number => lo + Math.random() * (hi - lo);

export class UiSounds {
  constructor(private readonly mx: Mixer) {}

  ui(sound: UiSound): void {
    const mx = this.mx;
    if (!mx.ctx || !mx.running) return;
    const d = mx.buses.ui;
    const t = mx.now() + 0.003;
    // Taps repeat a lot (menus, the phone, the shop): a small pitch / level spread keeps them from nagging.
    const p = jitterRatio(35);
    const v = jitterGain(0.8);
    switch (sound) {
      case 'click':
        // Bakelite toggle: a sharp tick on a small hollow body.
        ringTick(mx, d, t, 3100 * p, 4, 0.3 * v, 0.014, true);
        tone(mx, { type: 'sine', f0: 230 * p, f1: 120, sweepTime: 0.025, t, dur: 0.035, gain: 0.22 * v, dest: d, essential: true });
        break;
      case 'switch':
        // A heavier rotary switch: two detent clicks and a chunk.
        ringTick(mx, d, t, 2400 * p, 4, 0.32 * v, 0.016, true);
        ringTick(mx, d, t + 0.018, 1700 * p, 4, 0.26 * v, 0.02, true);
        tone(mx, { type: 'sine', f0: 170 * p, f1: 85, sweepTime: 0.03, t: t + 0.018, dur: 0.05, gain: 0.3 * v, dest: d, essential: true });
        break;
      case 'confirm':
        // A typewriter key and the little carriage bell.
        this.typeKey(t, 0.9);
        bellPartials(mx, d, t + 0.05, 2637, 0.32, 0.16, [1, 2.01, 3.2], true);
        break;
      case 'back':
        // Switch off: a duller click, falling.
        ringTick(mx, d, t, 1500 * p, 3, 0.28 * v, 0.02, true);
        tone(mx, { type: 'sine', f0: 320 * p, f1: 150, sweepTime: 0.05, t, dur: 0.07, gain: 0.22 * v, dest: d, essential: true });
        break;
      case 'tick':
        // Rotary-dial ratchet.
        ringTick(mx, d, t, 2800 * p, 6, 0.18 * v, 0.012, true);
        break;
      case 'stamp':
        tone(mx, { type: 'sine', f0: 150, f1: 42, sweepTime: 0.12, t, dur: 0.3, gain: 0.9, attack: 0.002, dest: d, essential: true });
        noiseBurst(mx, { kind: 'brown', t, dur: 0.12, gain: 0.5, filter: 'lowpass', f0: 600, dest: d, essential: true });
        noiseBurst(mx, { kind: 'white', t: t + 0.012, dur: 0.09, gain: 0.3, filter: 'bandpass', f0: 2200, q: 0.8, dest: d, essential: true });
        break;
      case 'cash':
        bellPartials(mx, d, t, 2093, 0.45, 0.3, [1, 2.01], true);
        bellPartials(mx, d, t + 0.08, 2637, 0.6, 0.3, [1, 2.01], true);
        break;
      case 'denied':
        // An old buzzer: two close, dark tones beating.
        tone(mx, { type: 'square', f0: 110, t, dur: 0.22, gain: 0.22, attack: 0.006, lp: 650, dest: d, essential: true });
        tone(mx, { type: 'square', f0: 116, t, dur: 0.22, gain: 0.22, attack: 0.006, lp: 650, dest: d, essential: true });
        break;
      case 'whoosh':
        noiseBurst(mx, { kind: 'pink', t, dur: 0.4, gain: 0.9, attack: 0.15, filter: 'bandpass', f0: 300, f1: 3200, q: 0.9, dest: d, essential: true });
        break;
      case 'type':
        this.typeKey(t, r(0.75, 1));
        break;
      case 'ring':
        this.phoneRing(t);
        break;
      case 'message':
        // Teleprinter chatter, then a soft bell: a message has come in.
        for (let i = 0; i < 5; i++) this.typeKey(t + i * r(0.045, 0.075), r(0.55, 0.85));
        bellPartials(mx, d, t + 0.42, 1760, 0.5, 0.14, [1, 2.76, 5.4], true);
        break;
    }
  }

  /** One typewriter key: the key clack, the type bar on the platen and the platen's thunk. */
  private typeKey(t: number, vel: number): void {
    const mx = this.mx;
    const d = mx.buses.ui;
    ringTick(mx, d, t, r(1900, 2500), 3, 0.2 * vel, 0.02, true);
    noiseBurst(mx, { kind: 'white', t: t + 0.006, dur: 0.012, gain: 0.18 * vel, filter: 'bandpass', f0: r(3000, 3800), q: 1.5, dest: d, essential: true });
    tone(mx, { type: 'sine', f0: r(150, 190), f1: 100, sweepTime: 0.02, t: t + 0.006, dur: 0.03, gain: 0.22 * vel, dest: d, essential: true });
  }

  /**
   * A GPO-style telephone bell: two small bells hammered by a clapper (~20 Hz), "ring-ring", for an
   * incoming call. Four sines per burst, amplitude-modulated, so it stays cheap.
   */
  private phoneRing(t: number): void {
    const mx = this.mx;
    const ctx = mx.ctx;
    if (!ctx) return;
    const d = mx.buses.ui;
    for (const start of [0, 0.6]) {
      const at = t + start;
      const dur = 0.42;
      const env = ctx.createGain();
      env.gain.setValueAtTime(0.0001, at);
      env.gain.linearRampToValueAtTime(0.11, at + 0.01);
      env.gain.setValueAtTime(0.11, at + dur - 0.03);
      env.gain.exponentialRampToValueAtTime(0.0001, at + dur + 0.25);
      const am = ctx.createGain();
      am.gain.value = 0.55;
      const clapper = ctx.createOscillator();
      clapper.type = 'triangle';
      clapper.frequency.value = 21;
      const depth = ctx.createGain();
      depth.gain.value = 0.45;
      clapper.connect(depth).connect(am.gain);
      am.connect(env).connect(d);
      const oscs: OscillatorNode[] = [clapper];
      for (const [f, g] of [
        [1046, 0.5],
        [2887, 0.18],
        [1318, 0.45],
        [3637, 0.15],
      ] as const) {
        const o = ctx.createOscillator();
        o.frequency.value = f;
        const og = ctx.createGain();
        og.gain.value = g;
        o.connect(og).connect(am);
        oscs.push(o);
        o.onended = () => {
          o.disconnect();
          og.disconnect();
        };
      }
      const end = at + dur + 0.3;
      for (const o of oscs) {
        o.start(at);
        o.stop(end);
      }
      clapper.onended = () => {
        clapper.disconnect();
        depth.disconnect();
        am.disconnect();
        env.disconnect();
      };
    }
  }

  /** Plays the stinger on the ui bus (so it is audible while paused). Ducking is done by the caller. */
  stinger(s: Stinger): void {
    const mx = this.mx;
    if (!mx.ctx || !mx.running) return;
    const d = mx.buses.ui;
    const t = mx.now() + 0.01;
    const score = STINGER_SCORES[s];
    if (score) {
      for (const note of score) {
        const ms = typeof note.midi === 'number' ? [note.midi] : note.midi;
        for (const m of ms) {
          const at = t + note.t;
          switch (note.inst) {
            case 'brass':
              brass(mx, d, at, m, note.dur, note.vel);
              break;
            case 'organ':
              organ(mx, d, at, m, note.dur, note.vel);
              break;
            case 'bell':
              bell(mx, d, at, m, note.dur, note.vel);
              break;
            case 'ping':
              tone(mx, { type: 'triangle', f0: midiToHz(m), t: at, dur: note.dur, gain: 0.3 * note.vel, attack: 0.001, dest: d });
              tone(mx, { type: 'sine', f0: midiToHz(m) * 2.01, t: at, dur: note.dur * 0.6, gain: 0.1 * note.vel, attack: 0.001, dest: d });
              break;
          }
        }
      }
      return;
    }
    switch (s) {
      case 'busted': {
        // Two siren whoops, then a descending brass sag.
        for (const off of [0, 0.4]) {
          tone(mx, { type: 'sawtooth', f0: 430, f1: 1150, sweepTime: 0.34, t: t + off, dur: 0.36, gain: 0.26, attack: 0.03, release: 0.1, lp: 1800, q: 1.2, dest: d });
          tone(mx, { type: 'square', f0: 431, f1: 1152, sweepTime: 0.34, t: t + off, dur: 0.36, gain: 0.08, attack: 0.03, release: 0.1, lp: 1500, dest: d });
        }
        brass(mx, d, t + 0.9, 62, 0.3, 1);
        brass(mx, d, t + 1.1, 59, 0.3, 1);
        brass(mx, d, t + 1.3, 55, 0.55, 1.1);
        organ(mx, d, t + 0.9, 38, 0.95, 0.9);
        break;
      }
      case 'wasted': {
        tone(mx, { type: 'sine', f0: 125, f1: 26, sweepTime: 1.4, t, dur: 1.6, gain: 0.9, attack: 0.01, dest: d });
        // Reverse-cymbal swell cut off at its peak.
        noiseBurst(mx, { kind: 'pink', t, dur: 1.15, gain: 0.35, attack: 1.0, filter: 'bandpass', f0: 300, f1: 5200, q: 0.9, dest: d });
        brass(mx, d, t + 1.1, 33, 0.9, 1.2);
        organ(mx, d, t + 1.1, 33, 0.9, 0.9);
        break;
      }
      case 'districtCard': {
        // Low soft swell: A2 / E3 / A3 sines with a slow attack.
        for (const f of [110, 164.8, 220]) {
          tone(mx, { type: 'sine', f0: f, t, dur: 1.6, gain: 0.16, attack: 0.55, release: 0.7, dest: d });
        }
        noiseBurst(mx, { kind: 'pink', t, dur: 1.2, gain: 0.05, attack: 0.5, filter: 'lowpass', f0: 500, dest: d });
        break;
      }
      default:
        break;
    }
  }
}
